import { useEffect, useMemo, useState } from 'react'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { apiGet, apiPost } from '../shared/api'
import { useI18n } from '../i18n'

interface StripePaymentProps {
  planId: string
  price: number
  currency: string
  onSuccess: () => void
  onError: (message: string) => void
}

declare global {
  interface Window {
    stripe: any
  }
}

let stripePromise: Promise<Stripe | null> | null = null

export default function StripePayment({ planId, price, currency, onSuccess, onError }: StripePaymentProps) {
  const { t } = useI18n()
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const config = await apiGet<{ publishableKey: string }>('/api/stripe/config')
        if (!stripePromise) {
          stripePromise = loadStripe(config.publishableKey)
        }
        const instance = await stripePromise
        setStripe(instance)
      } catch (e) {
        console.error(e)
        onError(t('subscription.stripeLoadError'))
      }
    })()
  }, [onError, t])

  useEffect(() => {
    // create PaymentIntent when component mounts or params change
    (async () => {
      try {
        const res = await apiPost<{ clientSecret: string }>(
          '/api/stripe/create-payment-intent',
          { planId, amount: Math.round(price * 100), currency: currency.toLowerCase() }
        )
        setClientSecret(res.clientSecret)
      } catch (e) {
        console.error('Failed to create payment intent', e)
        onError(t('subscription.paymentFailed'))
      }
    })()
  }, [planId, price, currency, onError, t])

  const options = useMemo(() => ({ clientSecret: clientSecret ?? '' }), [clientSecret])

  if (!stripe || !clientSecret) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-gray-600 text-center">{t('subscription.useStripe')}</div>
        <button disabled className="w-full py-3 px-6 rounded-xl font-medium text-white bg-gray-400 opacity-60">
          {t('subscription.loadingPayments')}
        </button>
      </div>
    )
  }

  return (
    <Elements stripe={stripe} options={options}>
      <PaymentForm onSuccess={onSuccess} onError={onError} planId={planId} t={t} />
    </Elements>
  )
}

function PaymentForm({ onSuccess, onError, planId, t }: { onSuccess: () => void; onError: (m: string) => void; planId: string; t: any }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!stripe || !elements) return
    setSubmitting(true)
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required'
      })
      if (error) {
        onError(error.message || 'Payment failed')
        return
      }
      if (paymentIntent?.status === 'succeeded') {
        const result = await apiPost('/api/stripe/confirm-payment', {
          paymentIntentId: paymentIntent.id,
          planId
        })
        if ((result as any).success) onSuccess()
        else onError((result as any).error || 'Confirm failed')
      }
    } catch (e: any) {
      onError(e?.message || 'Payment error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-600 text-center">{t('subscription.useStripe')}</div>
      <div className="border rounded-lg p-3 bg-white">
        <PaymentElement />
      </div>
      <button
        onClick={handleSubmit}
        disabled={submitting || !stripe || !elements}
        className="w-full py-3 px-6 rounded-xl font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:brightness-110 active:brightness-95 shadow-lg disabled:opacity-50"
      >
        {submitting ? t('subscription.processing') : `${t('subscription.payWith')} Stripe`}
      </button>
    </div>
  )
}